import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from '@/components/landing/LandingLayout';
import { PainAwareLandingHero } from '@/components/landing/PainAwareLandingHero';
import { PainSolutionBlock } from '@/components/landing/PainSolutionBlock';
import { ValuePropsSection } from '@/components/landing/ValuePropsSection';
import { BottomCtaBlock } from '@/components/landing/BottomCtaBlock';
import { AiSmartAutomationSection } from '@/components/landing/AiSmartAutomationSection';
import { FaqSection } from '@/components/landing/FaqSection';
import { BeforeAfterScenario } from '@/components/landing/BeforeAfterScenario';
import { AiAssistantSummary } from '@/components/landing/AiAssistantSummary';
import { StickyFloatingCTA } from '@/components/landing/StickyFloatingCTA';
import { TrustBadges } from '@/components/landing/TrustBadges';
import { StatsSection } from '@/components/landing/StatsSection';
import { TestimonialCarousel } from '@/components/landing/TestimonialCarousel';
import { RoiSection } from '@/components/landing/RoiSection';
import { ComparisonTable } from '@/components/landing/ComparisonTable';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, CheckCircle2, AlertTriangle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

const StopDoubleBookings = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const canonicalUrl = `https://rental.shift-box.com/${currentLang}/stop-airbnb-double-bookings`;
  const alternateLanguages = ['en', 'es', 'de', 'fr', 'it'];

  return (
    <>
      <Helmet>
        <title>{t('doubleBookings.seo.title')}</title>
        <meta name="description" content={t('doubleBookings.seo.description')} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags for multi-language support */}
        {alternateLanguages.map(lang => (
          <link
            key={lang}
            rel="alternate"
            hrefLang={lang}
            href={`https://rental.shift-box.com/${lang}/stop-airbnb-double-bookings`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://rental.shift-box.com/en/stop-airbnb-double-bookings"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                "name": "Rental Manager - Stop Double Bookings",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web"
              },
              {
                "@type": "FAQPage",
                "mainEntity": (t('doubleBookings.seo.faq', { returnObjects: true }) as any[]).map((faq: any) => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              }
            ]
          })}
        </script>
      </Helmet>

      <LandingLayout>
        <StickyFloatingCTA />

        {/* Hero - Enhanced with badge and animations */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-destructive/5 via-background to-primary/5 animate-gradient-animated"></div>
          <div className="container mx-auto px-4 py-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-fade-in">
                <Badge className="mb-4 bg-destructive/10 text-destructive border-destructive/20">
                  {t('doubleBookings.hero.badge')}
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  {t('doubleBookings.hero.h1')}
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  {t('doubleBookings.hero.subtitle')}
                </p>
                <Button size="lg" onClick={() => navigate('/auth')} className="text-lg px-8 py-6">
                  {t('doubleBookings.hero.ctaText')}
                </Button>
              </div>
              <div className="relative animate-fade-in" style={{ animationDelay: '200ms' }}>
                <img 
                  src="/screenshots/properties.webp" 
                  alt={t('doubleBookings.hero.imageAlt')}
                  className="rounded-lg shadow-2xl border border-border"
                />
                <div className="absolute -bottom-4 -right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-lg shadow-lg animate-pulse">
                  <p className="text-sm font-semibold">{t('doubleBookings.hero.imageBadge')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <TrustBadges />

        {/* Stats Section */}
        <section className="py-16 bg-gradient-to-b from-background via-destructive/5 to-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {(t('doubleBookings.stats', { returnObjects: true }) as any[]).map((stat, index) => (
                <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    {index === 0 ? <AlertTriangle className="w-8 h-8 text-destructive" /> :
                     index === 3 ? <Clock className="w-8 h-8 text-primary" /> :
                     <CheckCircle2 className="w-8 h-8 text-primary" />}
                  </div>
                  <div className="text-4xl font-bold text-foreground mb-2">{stat.value}</div>
                  <p className="text-muted-foreground text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Unique focus paragraph */}
        <section className="py-8 container mx-auto px-4 max-w-3xl">
          <p className="text-lg text-center text-muted-foreground">
            {t('doubleBookings.focus')}
          </p>
        </section>

        {/* Pain/Solution Section */}
        <section className="py-16 container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">
            {t('doubleBookings.pain.title')}
          </h2>
          <PainSolutionBlock
            problemTitle={t('doubleBookings.pain.problemTitle')}
            problemBullets={t('doubleBookings.pain.problemBullets', { returnObjects: true }) as string[]}
            solutionTitle={t('doubleBookings.pain.solutionTitle')}
            solutionBullets={t('doubleBookings.pain.solutionBullets', { returnObjects: true }) as string[]}
          />
        </section>

        {/* AI/Smart Automation - Enhanced */}
        <section className="py-16 bg-gradient-to-r from-primary/5 to-destructive/5">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <Badge className="mb-4">{t('doubleBookings.automation.badge')}</Badge>
              <h2 className="text-3xl font-bold">{t('doubleBookings.automation.title')}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <Card>
                <CardContent className="pt-6">
                  <ul className="space-y-4">
                    {(t('doubleBookings.automation.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-bold flex-shrink-0">{index + 1}</span>
                        <p className="text-sm">{feature}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              <div className="relative">
                <img 
                  src="/screenshots/portfolio.webp" 
                  alt={t('doubleBookings.automation.imageAlt')}
                  className="rounded-lg shadow-2xl border border-border"
                />
                <div className="absolute -top-4 -left-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg">
                  <p className="text-sm font-semibold">{t('doubleBookings.automation.imageBadge')}</p>
                </div>
              </div>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-6">
              {t('doubleBookings.automation.note')}
            </p>
          </div>
        </section>

        {/* Before/After Scenario */}
        <section className="py-16 bg-gradient-to-b from-muted/30 to-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {t('doubleBookings.beforeAfter.title')}
            </h2>
            <BeforeAfterScenario
              beforeText={t('doubleBookings.beforeAfter.before')}
              afterText={t('doubleBookings.beforeAfter.after')}
            />
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">{t('doubleBookings.testimonials.title')}</h2>
            <TestimonialCarousel 
              testimonials={t('doubleBookings.testimonials.items', { returnObjects: true }) as any[]}
            />
          </div>
        </section>

        {/* Value Props */}
        <ValuePropsSection />

        {/* ROI Section */}
        <section className="py-16 bg-gradient-to-b from-muted/30 to-background">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl font-bold mb-4 text-center">{t('doubleBookings.roi.title')}</h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              {t('doubleBookings.roi.subtitle')}
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {(t('doubleBookings.roi.cards', { returnObjects: true }) as any[]).map((card, index) => (
                <Card key={index} className={`hover:shadow-xl transition-all duration-300 ${index === 2 ? 'border-primary border-2' : ''}`}>
                  <CardContent className="pt-6">
                    <div className={`text-4xl font-bold mb-2 ${index === 2 ? 'text-primary' : 'text-destructive'}`}>{card.value}</div>
                    <p className="text-sm text-muted-foreground">{card.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-12">
              <div className="inline-block bg-primary/10 rounded-lg px-8 py-6">
                <p className="text-sm text-muted-foreground mb-2">{t('doubleBookings.roi.roiLabel')}</p>
                <p className="text-5xl font-bold text-primary">{t('doubleBookings.roi.roiValue')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Real-World Scenario Timeline */}
        <section className="py-16 bg-gradient-to-r from-primary/5 to-secondary/10">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl font-bold mb-4 text-center">{t('doubleBookings.timeline.title')}</h2>
            <p className="text-center text-muted-foreground mb-12">{t('doubleBookings.timeline.subtitle')}</p>
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>
              <div className="space-y-8">
                {(t('doubleBookings.timeline.events', { returnObjects: true }) as any[]).map((event, index) => (
                  <div key={index} className="relative flex items-start gap-4 animate-fade-in" style={{ animationDelay: `${index * 200}ms` }}>
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground flex-shrink-0">
                      {index === 0 ? <Clock className="w-8 h-8" /> :
                       index === 1 ? <AlertTriangle className="w-8 h-8" /> :
                       index === 2 ? <CheckCircle2 className="w-8 h-8" /> :
                       <Calendar className="w-8 h-8" />}
                    </div>
                    <Card className="flex-1">
                      <CardContent className="pt-6">
                        <p className="font-semibold mb-1">{event.time}</p>
                        <p className="text-muted-foreground">{event.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <ComparisonTable
          rows={[
            { feature: t('doubleBookings.comparison.features.0'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('doubleBookings.comparison.features.1'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('doubleBookings.comparison.features.2'), manual: false, spreadsheet: true, rentalManager: true },
            { feature: t('doubleBookings.comparison.features.3'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('doubleBookings.comparison.features.4'), manual: true, spreadsheet: false, rentalManager: true },
            { feature: t('doubleBookings.comparison.features.5'), manual: false, spreadsheet: false, rentalManager: true },
            { feature: t('doubleBookings.comparison.features.6'), manual: true, spreadsheet: false, rentalManager: true },
          ]}
        />

        {/* FAQ */}
        <FaqSection faqs={t('doubleBookings.faq', { returnObjects: true }) as any[]} />

        {/* AI Assistant Summary */}
        <AiAssistantSummary />

        {/* Bottom CTA */}
        <BottomCtaBlock />
      </LandingLayout>
    </>
  );
};

export default StopDoubleBookings;
